import React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@eluve/components';

import {
  type PatientStatusAction,
  type PatientStatusDimension,
} from './railPatient';

type DialogCopy = {
  title: (name: string) => string;
  description: string;
  confirmLabel: string;
};

const COPY: Record<
  PatientStatusDimension,
  Record<'set' | 'clear', DialogCopy>
> = {
  deceased: {
    set: {
      title: (name) => `Mark ${name} as deceased?`,
      description:
        'Their profile will show a deceased status. This can be undone later.',
      confirmLabel: 'Mark as deceased',
    },
    clear: {
      title: (name) => `Remove the deceased status for ${name}?`,
      description: 'Their profile will no longer show a deceased status.',
      confirmLabel: 'Remove status',
    },
  },
  'left-practice': {
    set: {
      title: (name) => `Mark ${name} as having left the practice?`,
      description:
        "Their profile will show that they've left the practice. This can be undone later.",
      confirmLabel: 'Mark as left',
    },
    clear: {
      title: (name) => `Mark ${name} as active?`,
      description:
        "Their profile will no longer show that they've left the practice.",
      confirmLabel: 'Mark as active',
    },
  },
};

export const PatientStatusConfirmDialog: React.FC<{
  action: PatientStatusAction | null;
  patientName: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ action, patientName, isPending, onConfirm, onCancel }) => {
  const copy = action
    ? COPY[action.dimension][action.set ? 'set' : 'clear']
    : null;
  return (
    <AlertDialog
      open={action !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy?.title(patientName)}</AlertDialogTitle>
          <AlertDialogDescription>{copy?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            data-testid="confirm-patient-status"
            onClick={(event) => {
              // Keep the dialog open while the write is in flight; the hook
              // clears the action (and closes it) when the write settles.
              event.preventDefault();
              onConfirm();
            }}
          >
            {isPending ? 'Saving…' : copy?.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
